import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { RollText } from '@/components/generals/roll-text'

type Variant = 'primary' | 'primary-dark' | 'dark' | 'outline' | 'outline-light' | 'text' | 'text-light'

interface CtaLinkProps {
    href: string
    children: ReactNode
    variant?: Variant
    icon?: 'right' | 'up-right'
    className?: string
}

const BUTTON = 'group inline-flex h-12 items-center justify-center gap-3 rounded-sm px-6 text-[13px] font-semibold uppercase tracking-[0.08em] transition-colors duration-500 ease-[var(--ease-out-expo)] focus-visible:outline-2 focus-visible:outline-offset-4'

/* Text links sit on a faint rule that fills from the left on hover. */
const TEXT = 'group relative inline-flex items-center gap-2 pb-1.5 text-[13px] font-semibold uppercase tracking-[0.08em] transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-current before:opacity-30 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-right after:scale-x-0 after:bg-current after:transition-transform after:duration-500 after:ease-[var(--ease-out-expo)] hover:after:origin-left hover:after:scale-x-100 focus-visible:after:scale-x-100'

const VARIANTS: Record<Variant, string> = {
    'primary': cn(BUTTON, 'bg-coral text-charcoal hover:bg-charcoal hover:text-cream focus-visible:outline-charcoal'),
    'primary-dark': cn(BUTTON, 'bg-coral text-charcoal hover:bg-cream focus-visible:outline-cream'),
    'dark': cn(BUTTON, 'bg-charcoal text-cream hover:bg-coral hover:text-charcoal focus-visible:outline-charcoal'),
    'outline': cn(BUTTON, 'border border-charcoal text-charcoal hover:bg-charcoal hover:text-cream focus-visible:outline-charcoal'),
    'outline-light': cn(BUTTON, 'border border-cream/60 text-cream hover:border-cream hover:bg-cream hover:text-charcoal focus-visible:outline-cream'),
    'text': cn(TEXT, 'text-charcoal'),
    'text-light': cn(TEXT, 'text-cream hover:text-coral')
}

const ICON_MOTION = 'absolute inset-0 size-4 transition-transform duration-500 ease-[var(--ease-out-expo)] motion-reduce:transition-none'

export const CtaLink: FC<CtaLinkProps> = ({
    href,
    children,
    variant = 'primary',
    icon = 'right',
    className
}): ReactNode => {
    const Icon = icon === 'up-right' ? ArrowUpRight : ArrowRight
    const diagonal = icon === 'up-right'

    return (
        <Link href={href} className={cn(VARIANTS[variant], className)}>
            <RollText>{children}</RollText>
            <span aria-hidden='true' className='relative size-4 shrink-0 overflow-hidden'>
                <Icon
                    className={cn(
                        ICON_MOTION,
                        diagonal ? 'group-hover:-translate-y-full group-hover:translate-x-full' : 'group-hover:translate-x-full'
                    )}
                />
                <Icon
                    className={cn(
                        ICON_MOTION,
                        diagonal ? '-translate-x-full translate-y-full group-hover:translate-x-0 group-hover:translate-y-0' : '-translate-x-full group-hover:translate-x-0'
                    )}
                />
            </span>
        </Link>
    )
}
